const DEADZONE = 0.19;
const MAX_MAG = 0.95;

/** Standard-mapping button indices. */
export const PadButton = {
  A: 0,
  B: 1,
  X: 2,
  Y: 3,
  LB: 4,
  RB: 5,
  Back: 8,
  Start: 9,
  DpadUp: 12,
  DpadDown: 13,
  DpadLeft: 14,
  DpadRight: 15,
} as const;

export interface PadState {
  connected: boolean;
  stickX: number;
  stickY: number;
  buttons: boolean[];
  activity: boolean;
}

/**
 * Polls navigator.getGamepads() fresh every tick — in Chrome the returned
 * objects are snapshots, not live references; caching one is the classic bug.
 * Pads are invisible until the user presses a button (browser privacy), so we
 * poll unconditionally and never gate on the connect event.
 */
export class GamepadSource {
  private state: PadState = {
    connected: false, stickX: 0, stickY: 0, buttons: [], activity: false,
  };
  /** Sticky pick: last pad index that actually produced input. */
  private activeIndex = -1;

  poll(): PadState {
    const pads = navigator.getGamepads ? navigator.getGamepads() : [];
    // Prefer whichever pad is producing input right now — devices like the
    // Steam Deck expose multiple pads and an inert virtual "standard" one can
    // shadow the pad the real sticks report through. Selection is sticky so a
    // momentarily idle pad isn't dropped between polls.
    let pad: Gamepad | null = null;
    for (const p of pads) {
      if (!p) continue; // sparse arrays with null holes on all browsers
      if (padActive(p)) { pad = p; this.activeIndex = p.index; break; }
    }
    if (!pad && this.activeIndex >= 0) {
      pad = pads[this.activeIndex] ?? null;
      if (!pad) this.activeIndex = -1;
    }
    if (!pad) {
      for (const p of pads) {
        if (!p) continue;
        if (p.mapping === 'standard') { pad = p; break; }
        pad ??= p; // non-standard fallback: sticks/face buttons usually still sane
      }
    }

    const s = this.state;
    if (!pad) {
      s.connected = false;
      s.stickX = 0;
      s.stickY = 0;
      s.buttons.length = 0;
      s.activity = false;
      return s;
    }

    s.connected = true;
    [s.stickX, s.stickY] = radialDeadzone(pad.axes[0] ?? 0, pad.axes[1] ?? 0);

    s.activity = s.stickX !== 0 || s.stickY !== 0;
    s.buttons.length = pad.buttons.length;
    for (let i = 0; i < pad.buttons.length; i++) {
      const down = pad.buttons[i]?.pressed ?? false;
      s.buttons[i] = down;
      if (down) s.activity = true;
    }
    return s;
  }
}

/**
 * Any pressed button, or the left stick (the only axes we consume) outside the
 * deadzone. Deliberately ignores other axes: non-standard mappings park trigger
 * axes at -1.0 at rest, which would make an idle pad look permanently active.
 */
function padActive(p: Gamepad): boolean {
  for (const b of p.buttons) if (b.pressed) return true;
  return Math.hypot(p.axes[0] ?? 0, p.axes[1] ?? 0) > DEADZONE;
}

/**
 * Radial deadzone with rescale: DZ→0, MAX→1, direction preserved (per-axis
 * deadzones snap diagonals to 45° and cross-shape the response).
 */
export function radialDeadzone(x: number, y: number): [number, number] {
  const m = Math.hypot(x, y);
  if (m < DEADZONE) return [0, 0];
  const scaled = Math.min((m - DEADZONE) / (MAX_MAG - DEADZONE), 1) / m;
  return [x * scaled, y * scaled];
}
